<template>
  <div
    class="relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800"
  >
    <!-- Main Carousel Container -->
    <div
      class="relative h-[550px] sm:h-[600px] md:h-[600px] lg:h-[700px] xl:h-screen xl:max-h-[800px]"
    >
      <!-- Slides Container -->
      <transition-group name="fade" tag="div" class="h-full">
        <div
          v-for="(slide, index) in slides"
          :key="index"
          v-show="currentSlide === index"
          class="absolute inset-0 transition-all duration-700"
        >
          <!-- Background Image with Overlay -->
          <div class="absolute inset-0 overflow-hidden">
            <img
              :src="slide.image"
              :alt="slide.title"
              class="w-full h-full object-cover transition-transform duration-[8000ms]"
              :class="getImageZoomClass(index)"
              loading="eager"
              :key="`slide-${index}`"
            />
            <div
              class="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-800/70 to-slate-900/60"
            ></div>
          </div>

          <!-- Content Container -->
          <div
            class="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10"
          >
            <div
              class="flex flex-col justify-center h-full max-w-2xl md:max-w-xl lg:max-w-2xl"
            >
              <!-- Service Badge -->
              <div
                class="inline-flex items-center space-x-3 mb-4 sm:mb-6 opacity-0 animate-slideUp"
                :style="{ animationDelay: '200ms' }"
              >
                <span
                  class="px-3 py-1 rounded-full bg-emerald-500/40 text-emerald-200 text-xs sm:text-sm font-medium drop-shadow-md"
                >
                  {{ slide.tag }}
                </span>
              </div>

              <!-- Title -->
              <h2
                class="text-3xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6 lg:mb-8 leading-tight opacity-0 animate-slideUp text-shadow-lg"
                :style="{ animationDelay: '400ms' }"
              >
                {{ slide.title }}
              </h2>

              <!-- Description -->
              <p
                class="text-white text-base sm:text-lg md:text-base lg:text-lg mb-6 sm:mb-8 lg:mb-10 leading-relaxed opacity-0 animate-slideUp line-clamp-3 md:line-clamp-4 lg:line-clamp-none text-shadow-sm"
                :style="{ animationDelay: '600ms' }"
              >
                {{ slide.description }}
              </p>
            </div>
          </div>
        </div>
      </transition-group>

      <!-- Navigation Arrows - Hidden on Small Screens -->
      <div
        class="hidden xl:flex absolute inset-0 items-center justify-between p-4"
      >
        <button
          @click="prevSlide"
          class="p-2 rounded-full bg-slate-800/50 text-white hover:bg-slate-700/50 transition-all duration-300 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-900 shadow-lg"
          aria-label="Previous slide"
        >
          <svg
            class="w-5 h-5 sm:w-6 sm:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button
          @click="nextSlide"
          class="p-2 rounded-full bg-[#2c5e1a]/50 text-white hover:bg-[#2c5e1a]/70 transition-all duration-300 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-900 shadow-lg"
          aria-label="Next slide"
        >
          <svg
            class="w-5 h-5 sm:w-6 sm:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      <!-- Progress Indicators -->
      <div
        class="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 flex items-center space-x-3 md:space-x-4"
      >
        <button
          v-for="(_, index) in slides"
          :key="index"
          @click="goToSlide(index)"
          class="group relative h-2 md:h-3 transition-all duration-300 focus:outline-none"
          :class="[
            currentSlide === index
              ? 'w-8 md:w-10 bg-emerald-400'
              : 'w-2 md:w-3 bg-slate-400/70 hover:bg-slate-300/70',
            'rounded-full overflow-hidden shadow-md',
          ]"
          :aria-label="`Go to slide ${index + 1}`"
          :aria-current="currentSlide === index"
        >
          <div
            class="absolute inset-0 bg-emerald-400 transform origin-left transition-transform duration-5000"
            :class="{ 'scale-x-100': currentSlide === index }"
            :style="{
              transform: currentSlide === index ? 'scaleX(1)' : 'scaleX(0)',
            }"
          ></div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AgricultureCarousel",
  data() {
    return {
      currentSlide: 0,
      slides: [
        {
          tag: "Bridging Gaps in Agriculture",
          title: "Empowering Local Farmers",
          description:
            "Connect with Village Loan & Savings Associations (VLSAs) to access affordable financing for your farm. Our platform brings financial services directly to rural communities.",
          image: require("@/assets/images/empower1.jpeg"),
          zoomDirection: "in", // Direction of zoom
        },
        {
          tag: "Market Access",
          title: "Connect Directly to Buyers",
          description:
            "Eliminate middlemen and sell your produce at fair prices through our digital marketplace. Connect with processors, retailers, and consumers to maximize your farm's profitability.",
          image: require("@/assets/images/buyers.jpeg"),
          zoomDirection: "out", // Direction of zoom
        },
        {
          tag: "Smart Farming",
          title: "Data-Driven Agriculture",
          description:
            "Access our Production Management Information System (PMIS) to track crops, optimize inputs, and increase yields. Make informed decisions based on real-time data and analytics.",
          image: require("@/assets/images/dataagric1.jpeg"),
          zoomDirection: "in", // Direction of zoom
        },
        {
          tag: "Weather Intelligence",
          title: "Stay Ahead of Climate Changes",
          description:
            "Receive localized weather forecasts and advisories to protect your crops and plan farming activities. Minimize weather-related risks with timely alerts and recommendations.",
          image: require("@/assets/images/climate2.jpeg"),
          zoomDirection: "out", // Direction of zoom
        },
        {
          tag: "Transport Solutions",
          title: "Move Your Produce Efficiently",
          description:
            "Find reliable transportation to get your produce to market fresh and on time. Connect with local transporters or join forces with other farmers for shared logistics.",
          image: require("@/assets/images/transport.jpeg"),
          zoomDirection: "in", // Direction of zoom
        },
        {
          tag: "Financial Security",
          title: "Plan for Tomorrow's Harvest",
          description:
            "Secure your future with agricultural pension plans designed specifically for farmers. Build wealth while farming and ensure a comfortable retirement after years of hard work.",
          image: require("@/assets/images/harvest.jpeg"),
          zoomDirection: "out", // Direction of zoom
        },
        {
          tag: "Banking Integration",
          title: "Modern Financial Services",
          description:
            "Access banking services directly through our platform. Apply for loans, make payments, and manage your farm finances without leaving your community.",
          image: require("@/assets/images/savings.jpeg"),
          zoomDirection: "in", // Direction of zoom
        },
      ],
      autoplayInterval: null,
      zoomTimers: {},
      zoomStates: {},
    };
  },
  mounted() {
    this.startAutoplay();
    this.handleTouchEvents();
    this.initializeZoomStates();
    this.startZoomEffect(this.currentSlide);
  },
  beforeUnmount() {
    this.stopAutoplay();
    this.removeTouchEvents();
    this.clearAllZoomTimers();
  },
  methods: {
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      this.resetAutoplay();
      this.startZoomEffect(this.currentSlide);
    },
    prevSlide() {
      this.currentSlide =
        (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      this.resetAutoplay();
      this.startZoomEffect(this.currentSlide);
    },
    goToSlide(index) {
      this.currentSlide = index;
      this.resetAutoplay();
      this.startZoomEffect(this.currentSlide);
    },
    startAutoplay() {
      this.autoplayInterval = setInterval(this.nextSlide, 5000);
    },
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
      }
    },
    resetAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    },
    handleTouchEvents() {
      this.touchStartX = 0;
      this.touchEndX = 0;

      const element = document.querySelector(".h-full");
      if (element) {
        element.addEventListener("touchstart", this.handleTouchStart, {
          passive: true,
        });
        element.addEventListener("touchend", this.handleTouchEnd, {
          passive: true,
        });
      }
    },
    removeTouchEvents() {
      const element = document.querySelector(".h-full");
      if (element) {
        element.removeEventListener("touchstart", this.handleTouchStart);
        element.removeEventListener("touchend", this.handleTouchEnd);
      }
    },
    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
    },
    handleTouchEnd(event) {
      this.touchEndX = event.changedTouches[0].clientX;
      this.handleSwipe();
    },
    handleSwipe() {
      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }
    },

    // Zoom effect methods
    initializeZoomStates() {
      this.slides.forEach((_, index) => {
        this.zoomStates[index] = "initial";
      });
    },

    startZoomEffect(index) {
      // Clear any existing zoom timer for this slide
      if (this.zoomTimers[index]) {
        clearTimeout(this.zoomTimers[index]);
      }

      // Reset the zoom state
      this.zoomStates[index] = "initial";

      // Set a small delay to ensure the initial state is applied
      this.$nextTick(() => {
        // After a small delay, start the zoom
        this.zoomTimers[index] = setTimeout(() => {
          this.zoomStates[index] = "zooming";
        }, 50);
      });
    },

    getImageZoomClass(index) {
      const direction = this.slides[index].zoomDirection || "in";

      if (this.currentSlide !== index) {
        return "";
      }

      if (this.zoomStates[index] === "initial") {
        return direction === "in" ? "scale-100" : "scale-110";
      } else {
        return direction === "in" ? "scale-110" : "scale-100";
      }
    },

    clearAllZoomTimers() {
      Object.keys(this.zoomTimers).forEach((key) => {
        clearTimeout(this.zoomTimers[key]);
      });
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(1.05);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slideUp {
  animation: slideUp 0.7s ease-out forwards;
}

.duration-5000 {
  transition-duration: 5000ms;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-4 {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.h-full {
  -webkit-user-select: none;
  user-select: none;
  touch-action: pan-y pinch-zoom;
}

/* Text shadow for better readability */
.text-shadow-sm {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.text-shadow-lg {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.7);
}
</style>
